import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { Vehicle, PersonOfInterest, DriverLicense } from '../types';
import mapboxgl from 'mapbox-gl';

mapboxgl.accessToken = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';

const API_URL = process.env.REACT_APP_API_URL || '';

// Mapping function for vehicle address
const mapAddressDataForVehicle = (addressData: any) => {
  return {
    address_line1: addressData.address_line1 || addressData.street || addressData.address_line_1 || addressData.line1 || '',
    address_line2: addressData.address_line2 || addressData.address_line_2 || addressData.line2 || '',
    suburb: addressData.suburb || addressData.state || addressData.area || '',
    city_town: addressData.city_town || addressData.city || addressData.town || '',
    postal_code: addressData.postal_code || addressData.zipCode || addressData.zip || '',
    region: addressData.region || addressData.province || addressData.state || '',
    country: addressData.country || 'South Africa'
  };
};

// Year Picker Component
interface YearPickerProps {
  value: number | undefined;
  onChange: (year: number) => void;
  disabled?: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const startYear = 1900;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);

  const handleYearSelect = (year: number) => {
    onChange(year);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ''}
        readOnly
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm cursor-pointer bg-white"
        placeholder="Select year"
        disabled={disabled}
      />
      {!disabled && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div className="py-1">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 ${
                    value === year ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface MapboxFeature {
  id: string;
  place_name: string;
  text: string;
  context?: Array<{ id: string; text: string }>;
}

// Constants (matching the modal exactly)
const parkingLocations = [
  { id: '1', name: 'Garage' },
  { id: '2', name: 'Car Park' },
  { id: '3', name: 'Marked parking area' },
  { id: '4', name: 'Undercover parking' },
  { id: '5', name: 'Behind close gates' },
  { id: '6', name: 'House carport' },
  { id: '7', name: 'Security estate carport' },
  { id: '8', name: 'Street' },
  { id: '9', name: 'Driveway' },
  { id: '10', name: 'Security Complex' },
  { id: '11', name: 'Other' },
];

const coverTypes = [
  { id: '1', name: 'Comprehensive' },
  { id: '2', name: 'Third Party, Fire & Theft' },
  { id: '3', name: 'Third Party Only' },
  { id: '4', name: 'Theft Cover Excluded' },
];

const safetyFeatures = [
  { id: '1', name: 'Alarm' },
  { id: '2', name: 'Tracking Device' },
  { id: '3', name: 'Factory Fitted Alarm' },
  { id: '4', name: 'Data Dot' },
  { id: '5', name: 'Immobiliser' },
  { id: '6', name: 'Smash and Grab' },
];

const noClaimBonusOptions = [
  { id: '1', name: '12 Months  (1 Year)' },
  { id: '2', name: '24 Months  (2 Year)' },
  { id: '3', name: '36 Months  (3 Year)' },
  { id: '4', name: '48 Months  (4 Year)' },
  { id: '5', name: '60 Months  (5 Year)' },
  { id: '6', name: '72 Months  (6 Year)' },
  { id: '7', name: '84 Months  (7 Year)' },
  { id: '8', name: '96 Months  (8 Year)' },
  { id: '9', name: '108 Months  (9 Year)' },
  { id: '10', name: '120 Months  (10 Year)' },
];

const AddVehiclePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId'); // ✅ ADD THIS
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddVehiclePage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId, // ✅ ADD THIS
    fromPolicy // ✅ ADD THIS
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localSaveMessage, setLocalSaveMessage] = useState('');
  
  // Client data state
  const [clientAddress, setClientAddress] = useState({
    address_line1: '',
    address_line2: '',
    suburb: '',
    city_town: '',
    postal_code: '',
    region: '',
    country: '',
  });
  
  const [clientData, setClientData] = useState<{
    id: number;
    first_name: string;
    last_name: string;
    drivers_license_codes?: DriverLicense[];
  } | null>(null);
  
  // Form state
  const [vehicleType, setVehicleType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [variant, setVariant] = useState('');
  const [color, setColor] = useState('');
  const [usage, setUsage] = useState('');
  const [vinNumber, setVinNumber] = useState('');
  const [engineNumber, setEngineNumber] = useState('');
  const [mmcode, setMmcode] = useState('');
  const [insuredValue, setInsuredValue] = useState('');
  const [selectedCoverType, setSelectedCoverType] = useState('');
  const [useRetailValue, setUseRetailValue] = useState(false);
  const [excessBuyBack, setExcessBuyBack] = useState(false);
  const [creditShortfall, setCreditShortfall] = useState(false);
  const [carHire, setCarHire] = useState(false);
  const [selectedParking, setSelectedParking] = useState('');
  const [selectedNightParking, setSelectedNightParking] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedOtherDrivers, setSelectedOtherDrivers] = useState('');
  const [selectedSafetyFeatures, setSelectedSafetyFeatures] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [suburb, setSuburb] = useState('');
  const [cityTown, setCityTown] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('');
  const [overnightParkingElsewhere, setOvernightParkingElsewhere] = useState(false);
  const [premium, setPremium] = useState('');
  const [excess, setExcess] = useState('');
  const [selectedNoClaimBonus, setSelectedNoClaimBonus] = useState('');
  
  const [overnightAddress, setOvernightAddress] = useState({
    address_line1: '',
    address_line2: '',
    suburb: '',
    city_town: '',
    postal_code: '',
    region: '',
    country: '',
  });
  
  // Overnight address autocomplete
  const [overnightSuggestions, setOvernightSuggestions] = useState<MapboxFeature[]>([]);
  const [showOvernightSuggestions, setShowOvernightSuggestions] = useState(false);
  const overnightInputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [clientError, setClientError] = useState('');
  const [error, setError] = useState('');
  const [localPersonsOfInterest, setLocalPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  
  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
  });

  // Person Modal State - ADD THIS BLOCK
  const [isPersonModalVisible, setIsPersonModalVisible] = useState(false);
  const [personFormData, setPersonFormData] = useState<Partial<PersonOfInterest>>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: 'active',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });
  const [personErrors, setPersonErrors] = useState({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });

  // Restore vehicle form data from localStorage when component mounts
  useEffect(() => {
    const savedData = localStorage.getItem(`vehicle_form_draft_${clientId}`);
    if (savedData) {
      try {
        const formData = JSON.parse(savedData);
        console.log('🔄 Restoring saved vehicle form data');
        
        setVehicleType(formData.vehicleType || '');
        setRegistrationNumber(formData.registrationNumber || '');
        setMake(formData.make || '');
        setModel(formData.model || '');
        setYear(formData.year || '');
        setVariant(formData.variant || '');
        setColor(formData.color || '');
        setUsage(formData.usage || '');
        setVinNumber(formData.vinNumber || '');
        setEngineNumber(formData.engineNumber || '');
        setMmcode(formData.mmcode || '');
        setInsuredValue(formData.insuredValue || '');
        setSelectedCoverType(formData.selectedCoverType || '');
        setUseRetailValue(formData.useRetailValue || false);
        setExcessBuyBack(formData.excessBuyBack || false);
        setCreditShortfall(formData.creditShortfall || false);
        setCarHire(formData.carHire || false);
        setSelectedParking(formData.selectedParking || '');
        setSelectedNightParking(formData.selectedNightParking || '');
        setSelectedOwner(formData.selectedOwner || '');
        setSelectedDriver(formData.selectedDriver || '');
        setSelectedOtherDrivers(formData.selectedOtherDrivers || '');
        setSelectedSafetyFeatures(formData.selectedSafetyFeatures || '');
        setAddressLine1(formData.addressLine1 || '');
        setAddressLine2(formData.addressLine2 || '');
        setSuburb(formData.suburb || '');
        setCityTown(formData.cityTown || '');
        setPostalCode(formData.postalCode || '');
        setRegion(formData.region || '');
        setCountry(formData.country || 'South Africa');
        setOvernightParkingElsewhere(formData.overnightParkingElsewhere || false);
        setPremium(formData.premium || '');
        setExcess(formData.excess || '');
        setSelectedNoClaimBonus(formData.selectedNoClaimBonus || '');
        
        if (formData.overnightAddress) {
          setOvernightAddress(formData.overnightAddress);
        }
        
        if (formData.currencyDisplay) {
          setCurrencyDisplay(formData.currencyDisplay);
        }
        
        // Clear the saved data after restoring
        localStorage.removeItem(`vehicle_form_draft_${clientId}`);
        console.log('🗑️ Cleared saved vehicle form data from localStorage');
      } catch (error) {
        console.error('Error restoring vehicle form data:', error);
      }
    }
  }, [clientId]);
  
  const effectivePersonsOfInterest = localPersonsOfInterest;
  const isLoadingEffective = loading && localPersonsOfInterest.length === 0;
  
  // Fetch overnight address suggestions
  const fetchOvernightSuggestions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setOvernightSuggestions([]);
      setShowOvernightSuggestions(false);
      return;
    }
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&types=address&country=ZA&limit=10`
      );
      const data = await response.json();
      setOvernightSuggestions(data.features || []);
      setShowOvernightSuggestions(true);
    } catch (error) {
      console.error('Error fetching overnight address suggestions:', error);
      setOvernightSuggestions([]);
      setShowOvernightSuggestions(false);
    }
  }, []);
  
  const handleOvernightAddressSelect = (feature: MapboxFeature) => {
    let street = feature.place_name.split(',')[0].trim();
    let suburb = '';
    let city = '';
    let province = '';
    let postal = '';
    feature.context?.forEach((ctx) => {
      const ctxId = ctx.id;
      if (ctxId.startsWith('place.')) city = ctx.text;
      else if (ctxId.startsWith('locality.') || ctxId.startsWith('neighborhood.')) suburb = ctx.text;
      else if (ctxId.startsWith('region.')) province = ctx.text.replace(/\d+/g, '').trim();
      else if (ctxId.startsWith('postcode.')) postal = ctx.text;
    });
    if (!suburb) {
      const parts = feature.place_name.split(',');
      if (parts.length >= 2) {
        const potential = parts[1]?.trim();
        if (potential && potential !== city && !/^\d+$/.test(potential)) {
          suburb = potential;
        }
      }
    }
    setOvernightAddress({
      address_line1: street,
      address_line2: '',
      suburb: suburb,
      city_town: city,
      postal_code: postal,
      region: province,
      country: 'South Africa',
    });
    setShowOvernightSuggestions(false);
  };
  
  const handleOvernightSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => fetchOvernightSuggestions(value), 300);
  };
  
  const handleOvernightAddressChange = (field: keyof typeof overnightAddress, value: string) => {
    setOvernightAddress(prev => ({ ...prev, [field]: value }));
  };
  
  // Formatting functions
  const formatCurrencyInput = useCallback((value: string): string => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d.]/g, '');
    const parts = numericValue.split('.');
    let wholePart = parts[0] || '';
    let decimalPart = parts[1] || '';
    if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
    if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  }, []);
  
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/,/g, ''));
  };
  
  const formatDriverLicensesForDisplay = (licenses: DriverLicense[] | undefined): string => {
    if (!licenses || !Array.isArray(licenses) || licenses.length === 0) return '';
    const validLicenses = licenses.filter(license => license && license.code && license.code.trim() !== '');
    if (validLicenses.length === 0) return '';
    const formattedLicenses = validLicenses.map(license => {
      const code = license.code?.trim() || '';
      const date = license.dateOfFirstIssue;
      let formattedDate = '';
      if (date) {
        try {
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            formattedDate = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
          } else {
            formattedDate = date;
          }
        } catch (error) { formattedDate = date; }
      }
      return formattedDate ? `${code} - ${formattedDate}` : code;
    }).filter(formatted => formatted.trim() !== '');
    if (formattedLicenses.length === 0) return '';
    return ` (${formattedLicenses.join(', ')})`;
  };
  
  const parseLicenseData = (raw: any): DriverLicense[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map((item: any) => {
        const code = String(item?.code ?? item?.license_code ?? '').trim();
        return code ? {
          id: item?.id,
          code,
          dateOfFirstIssue: item?.dateOfFirstIssue ?? item?.date_of_first_issue ?? item?.issue_date ?? '',
          expiresOn: item?.expiresOn ?? item?.expires_on ?? item?.expiry_date ?? '',
        } : null;
      }).filter(l => !!l) as DriverLicense[];
    }
    if (typeof raw === 'string') {
      const str = raw.trim();
      if (!str) return [];
      try {
        const parsed = JSON.parse(str);
        return parseLicenseData(parsed);
      } catch {
        if (str.includes(',') || str.includes('|')) {
          return str.split(',').map((part) => part.trim()).filter((part) => part).map((entry) => {
            const parts = entry.split('|').map((p) => p.trim());
            const code = parts[0] || '';
            const issue = parts[1] || '';
            const expiry = parts[2] || '';
            return code ? { code, dateOfFirstIssue: issue, expiresOn: expiry } : null;
          }).filter(l => !!l) as DriverLicense[];
        }
        if (str.includes(',')) {
          return str.split(',').map((code) => code.trim()).filter((code) => code).map((code) => ({ code, dateOfFirstIssue: '', expiresOn: '' }));
        }
        return [{ code: str, dateOfFirstIssue: '', expiresOn: '' }];
      }
    }
    if (raw && typeof raw === 'object' && (raw.code || raw.license_code)) {
      const code = String(raw.code ?? raw.license_code ?? '').trim();
      return code ? [{ code, dateOfFirstIssue: raw.dateOfFirstIssue ?? '', expiresOn: raw.expiresOn ?? '' }] : [];
    }
    return [];
  };
  
  const getAllOptions = () => {
    const options = [];
    if (clientData) {
      const clientLicenseString = formatDriverLicensesForDisplay(clientData.drivers_license_codes || []);
      options.push({
        id: clientData.id,
        name: clientData.first_name,
        surname: clientData.last_name,
        drivers_license_codes: clientData.drivers_license_codes || [],
        isClient: true,
        displayName: `${clientData.first_name} ${clientData.last_name}${clientLicenseString}`
      });
    } else {
      options.push({
        id: clientId,
        name: 'Client',
        surname: `#${clientId}`,
        drivers_license_codes: [],
        isClient: true,
        displayName: `Client #${clientId}`
      });
    }
    effectivePersonsOfInterest.forEach(person => {
      if (person.status && person.status.toLowerCase() === 'inactive') return;
      let licenses: DriverLicense[] = [];
      if (person.drivers_license_codes) {
        if (Array.isArray(person.drivers_license_codes)) {
          licenses = person.drivers_license_codes;
        } else if (typeof person.drivers_license_codes === 'string') {
          try {
            const parsed = JSON.parse(person.drivers_license_codes);
            licenses = Array.isArray(parsed) ? parsed : [];
          } catch { licenses = []; }
        }
      }
      const licenseString = formatDriverLicensesForDisplay(licenses);
      options.push({
        ...person,
        drivers_license_codes: licenses,
        isClient: false,
        displayName: `${person.name || ''} ${person.surname || ''}${licenseString}`
      });
    });
    return options;
  };
  
  // Fetch data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  const fetchPersonsOfInterest = useCallback(async () => {
    if (!clientId) {
      setError('Client ID is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await fetchClientData(parseInt(clientId));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      const mappedPersons = data.map((person: any) => {
        let drivers_license_codes: DriverLicense[] = [];
        const licenseCode = person.drivers_license_code;
        const licenseCodes = person.drivers_license_codes;
        if (licenseCodes && Array.isArray(licenseCodes)) {
          drivers_license_codes = licenseCodes.map((license: any) => ({
            id: license.id || undefined,
            code: license.code || '',
            dateOfFirstIssue: license.dateOfFirstIssue || license.date_of_first_issue || '',
            expiresOn: license.expiresOn || license.expires_on || '',
          }));
        } else if (licenseCode) {
          drivers_license_codes = parseLicenseData(licenseCode);
        }
        return { ...person, drivers_license_codes };
      });
      setLocalPersonsOfInterest(mappedPersons);
    } catch (err) {
      console.error('Error fetching persons of interest:', err);
      setError(err instanceof Error ? err.message : 'Error loading persons of interest');
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  
  const fetchClientData = async (clientIdNum: number) => {
    setAddressLoading(true);
    setClientError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setClientError('Authentication required. Please log in again.');
        return null;
      }
      const clientResponse = await fetch(`${API_URL}/api/clients/${clientIdNum}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      if (clientResponse.status === 401) {
        setClientError('Authentication failed. Please log in again.');
        localStorage.removeItem('token');
        return null;
      }
      if (!clientResponse.ok) {
        setClientData({ id: clientIdNum, first_name: 'Client', last_name: `#${clientIdNum}`, drivers_license_codes: [] });
      } else {
        const clientDataJson = await clientResponse.json();
        const parsedDriverLicenses = parseLicenseData(clientDataJson.drivers_license_codes || clientDataJson.drivers_license_code);
        setClientData({
          id: clientDataJson.id,
          first_name: clientDataJson.name || clientDataJson.first_name || '',
          last_name: clientDataJson.surname || clientDataJson.last_name || '',
          drivers_license_codes: parsedDriverLicenses
        });
      }
      const addressResponse = await fetch(`${API_URL}/api/addresses/client/${clientIdNum}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      let addressData = null;
      if (addressResponse.ok && addressResponse.status !== 404) {
        addressData = await addressResponse.json();
      }
      if (addressData) {
        const mappedData = mapAddressDataForVehicle(addressData);
        setClientAddress(mappedData);
      }
      return addressData;
    } catch (err) {
      console.error('fetchClientData Error:', err);
      setClientData({ id: clientIdNum, first_name: 'Client', last_name: `#${clientIdNum}`, drivers_license_codes: [] });
      setClientError('Error fetching client data');
      return null;
    } finally {
      setAddressLoading(false);
    }
  };
  
  // Fetch existing vehicle if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      const fetchVehicle = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/vehicles/${editId}`, { headers: { Authorization: `Bearer ${token}` } });
          const vehicle = response.data.data || response.data;

          console.log('🔍 ===== VEHICLE DATA FROM BACKEND =====');
          console.log('🔍 Full vehicle object:', vehicle);
          console.log('🔍 overnight_parking_elsewhere:', vehicle.overnight_parking_elsewhere);
          console.log('🔍 All keys in vehicle:', Object.keys(vehicle));
          console.log('🔍 Overnight address fields check:', {
            'overnight_address_line1': vehicle.overnight_address_line1,
            'overnightAddressLine1': vehicle.overnightAddressLine1,
            'overnight_address_line2': vehicle.overnight_address_line2,
            'overnightAddressLine2': vehicle.overnightAddressLine2,
            'overnight_suburb': vehicle.overnight_suburb,
            'overnightSuburb': vehicle.overnightSuburb,
            'overnight_city_town': vehicle.overnight_city_town,
            'overnightCityTown': vehicle.overnightCityTown,
    });

          setVehicleType(vehicle.vehicle_type || '');
          setRegistrationNumber(vehicle.registration_number || '');
          setMake(vehicle.make || '');
          setModel(vehicle.model || '');
          setYear(vehicle.year?.toString() || '');
          setVariant(vehicle.variant || '');
          setColor(vehicle.color || '');
          setUsage(vehicle.usage || '');
          setVinNumber(vehicle.vin_number || '');
          setEngineNumber(vehicle.engine_number || '');
          setMmcode(vehicle.mmcode || '');
          setInsuredValue(vehicle.insured_value?.toString() || '');
          setSelectedCoverType(vehicle.cover_type || '');
          setUseRetailValue(vehicle.use_retail_value || false);
          setExcessBuyBack(vehicle.excess_buy_back || false);
          setCreditShortfall(vehicle.credit_shortfall || false);
          setCarHire(vehicle.car_hire || false);
          setSelectedParking(vehicle.day_parking_location || '');
          setSelectedNightParking(vehicle.night_parking_location || '');
          setSelectedOwner(vehicle.registered_owner || '');
          setSelectedDriver(vehicle.regular_driver || '');
          setSelectedOtherDrivers(vehicle.other_drivers || '');
          setSelectedSafetyFeatures(vehicle.safety_features || '');
          setAddressLine1(vehicle.address_line1 || '');
          setAddressLine2(vehicle.address_line2 || '');
          setSuburb(vehicle.suburb || '');
          setCityTown(vehicle.city_town || '');
          setPostalCode(vehicle.postal_code || '');
          setRegion(vehicle.region || '');
          setCountry(vehicle.country || '');
          setOvernightParkingElsewhere(vehicle.overnight_parking_elsewhere || false);
          setPremium(vehicle.premium?.toString() || '');
          setExcess(vehicle.excess?.toString() || '');
          setSelectedNoClaimBonus(vehicle.no_claim_bonus || '');
          setCurrencyDisplay({
            insuredValue: vehicle.insured_value ? formatCurrencyInput(vehicle.insured_value.toString()) : '',
            premium: vehicle.premium ? formatCurrencyInput(vehicle.premium.toString()) : '',
            excess: vehicle.excess ? formatCurrencyInput(vehicle.excess.toString()) : '',
          });
          if (vehicle.overnight_parking_elsewhere) {
            setOvernightAddress({
              address_line1: vehicle.overnight_address_line1 || vehicle.overnightAddressLine1 || '',
              address_line2: vehicle.overnight_address_line2 || vehicle.overnightAddressLine2 || '',
              suburb: vehicle.overnight_suburb || vehicle.overnightSuburb || '',
              city_town: vehicle.overnight_city_town || vehicle.overnightCityTown || '',
              postal_code: vehicle.overnight_postal_code || vehicle.overnightPostalCode || '',
              region: vehicle.overnight_region || vehicle.overnightRegion || '',
              country: vehicle.overnight_country || vehicle.overnightCountry || '',
            });
          }
        } catch (error) {
          console.error('Error fetching vehicle for edit:', error);
        }
      };
      fetchVehicle();
    }
  }, [quoteId, location.search, formatCurrencyInput, policyId, fromPolicy]); // ✅ Add policyId and fromPolicy
  
  useEffect(() => {
    if (clientId) {
      fetchClientData(parseInt(clientId));
      fetchPersonsOfInterest();
    }
  }, [clientId, fetchPersonsOfInterest]);
  
  const handleFillFromClient = async () => {
    try {
      setAddressLoading(true);
      setClientError('');
      const addressData = await fetchClientData(parseInt(clientId));
      if (addressData) {
        const mappedData = mapAddressDataForVehicle(addressData);
        setAddressLine1(mappedData.address_line1 || '');
        setAddressLine2(mappedData.address_line2 || '');
        setSuburb(mappedData.suburb || '');
        setCityTown(mappedData.city_town || '');
        setPostalCode(mappedData.postal_code || '');
        setRegion(mappedData.region || '');
        setCountry(mappedData.country || '');
        setLocalSaveMessage('✓ Client address filled successfully!');
        setTimeout(() => setLocalSaveMessage(''), 3000);
      } else {
        setLocalSaveMessage('⚠️ No address data available for this client');
        setTimeout(() => setLocalSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error in handleFillFromClient:', error);
      setLocalSaveMessage('❌ Failed to fetch address. Please try again.');
      setTimeout(() => setLocalSaveMessage(''), 5000);
    } finally {
      setAddressLoading(false);
    }
  };
  
  const validateForm = () => {
    const errors = [];
    if (!vehicleType) errors.push('Vehicle Type is required');
    if (!registrationNumber) errors.push('Registration Number is required');
    if (!make) errors.push('Make is required');
    if (!model) errors.push('Model is required');
    if (insuredValue && isNaN(parseFloat(insuredValue))) errors.push('Insured Value must be a valid number');
    if (premium && isNaN(parseFloat(premium))) errors.push('Premium must be a valid number');
    if (excess && isNaN(parseFloat(excess))) errors.push('Excess must be a valid number');
    if (year && (isNaN(parseInt(year)) || parseInt(year) < 1900 || parseInt(year) > new Date().getFullYear() + 1)) {
      errors.push('Year must be a valid year');
    }
    if (errors.length > 0) {
      setLocalSaveMessage(`Please fix the following errors:\n\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };
  
    const handleSaveClick = async () => {
    if (isSaving) {
      console.log('⚠️ Save already in progress, skipping...');
      return;
    }
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      // Clean the quoteId - use undefined instead of null
      let cleanQuoteId: number | undefined = undefined;
      if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
        cleanQuoteId = parseInt(quoteId);
      }
      
      // Helper function to convert to number
      const toNumber = (value: any): number => {
        if (value === undefined || value === null || value === '') return 0;
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      };
      
      const vehicleData = {
  id: editingId || `temp-${Date.now()}`,
  vehicle_type: vehicleType,
  registration_number: registrationNumber,
  make: make,
  model: model,
  year: year ? parseInt(year) : undefined,
  variant: variant,
  color: color,
  usage: usage,
  vin_number: vinNumber,
  engine_number: engineNumber,
  mmcode: mmcode,
  insured_value: toNumber(insuredValue),
  cover_type: selectedCoverType,
  use_retail_value: useRetailValue,
  excess_buy_back: excessBuyBack,
  credit_shortfall: creditShortfall,
  car_hire: carHire,
  day_parking_location: selectedParking,
  night_parking_location: selectedNightParking,
  registered_owner: selectedOwner,
  regular_driver: selectedDriver,
  other_drivers: selectedOtherDrivers,
  safety_features: selectedSafetyFeatures,
  address_line1: addressLine1,
  address_line2: addressLine2,
  suburb: suburb,
  city_town: cityTown,
  postal_code: postalCode,
  region: region,
  country: country,
  overnight_parking_elsewhere: overnightParkingElsewhere,
  premium: toNumber(premium),
  excess: toNumber(excess),
  no_claim_bonus: selectedNoClaimBonus,
  client_id: parseInt(clientId),
  quote_id: cleanQuoteId,
  // Overnight address fields
  overnight_address_line1: overnightAddress.address_line1 || '',
  overnight_address_line2: overnightAddress.address_line2 || '',
  overnight_suburb: overnightAddress.suburb || '',
  overnight_city_town: overnightAddress.city_town || '',
  overnight_postal_code: overnightAddress.postal_code || '',
  overnight_region: overnightAddress.region || '',
  overnight_country: overnightAddress.country || '',
} as any;  // ← Add "as any" to bypass TypeScript check
      
      console.log('🚗 Saving vehicle with data:', JSON.stringify(vehicleData, null, 2));
      
      const token = localStorage.getItem('token');
      
      // If no quote_id, save to localStorage (draft mode)
if (!cleanQuoteId) {
  console.log('💾 No quote ID - saving vehicle to localStorage');
  
  // ✅ Determine the storage key based on context
  let storageKey: string;
  
  // ✅ Only use policy key if we're explicitly coming from policy
  if (policyId && fromPolicy === true) {
    // Coming from Policy Detail page - save with policy ID key
    storageKey = `policy_${policyId}_vehicle`;
    console.log(`📂 Saving vehicle with policy key: ${storageKey}`);
  } else {
    // Coming from Quote/Create Quote page - save with client draft key
    storageKey = `client_${clientId}_vehicles_draft`;
    console.log(`📂 Saving vehicle with client draft key: ${storageKey}`);
  }
  
  const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  // Check if we're editing an existing draft
  let updatedDrafts;
  if (editingId && editingId.startsWith('temp-')) {
    // Update existing draft
    updatedDrafts = existingDrafts.map((item: any) => 
      item.id === editingId 
        ? { 
            ...item, 
            ...vehicleData, 
            id: editingId,
            isTemp: true,
            status: 'active',
            inception_date: new Date().toISOString().split('T')[0],
            product_type: 'vehicles'
          }
        : item
    );
  } else {
    // Add new draft
    updatedDrafts = [
      ...existingDrafts,
      {
        ...vehicleData,
        id: `temp-${Date.now()}`,
        isTemp: true,
        status: 'active',
        inception_date: new Date().toISOString().split('T')[0],
        product_type: 'vehicles'
      }
    ];
  }
  
  localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
  
  setLocalSaveMessage('Vehicle saved to draft!');
  
  // ✅ Navigate based on context
  setTimeout(() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Master quote mode - navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else {
      // Create quote mode
      navigate(`/create-quote/${clientId}`, { 
        state: { refreshProducts: true, hasDrafts: true }
      });
    }
  }, 1500);

      } else {
  // Has quote_id - save to database
  if (editingId) {
    await axios.put(`${API_URL}/api/vehicles/${editingId}`, vehicleData, { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    setLocalSaveMessage('Vehicle updated successfully!');
    
    // ✅ LOG ACTIVITY: Vehicle Updated
    if (cleanQuoteId) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const performedBy = user?.username || user?.email || 'System';
        
        await axios.post(
          `${API_URL}/api/quote-activity-logs`,
          {
            quote_id: cleanQuoteId,
            action_type: 'product_updated',
            description: `Vehicle updated: ${make || ''} ${model || ''}`.trim() || 'Vehicle',
            metadata: {
              productType: 'vehicle',
              productId: editingId,
              performedBy: performedBy,
              make: make || '',
              model: model || '',
              registrationNumber: registrationNumber || '',
              year: year || '',
              insuredValue: parseFloat(insuredValue) || 0,
              updatedAt: new Date().toISOString()
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Activity logged: vehicle_updated');
      } catch (logError) {
        console.error('❌ Failed to log vehicle update:', logError);
        // Don't fail the update if logging fails
      }
    }
  } else {
    await axios.post(`${API_URL}/api/vehicles`, vehicleData, { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    setLocalSaveMessage('Vehicle added successfully!');
    
    // ✅ LOG ACTIVITY: Vehicle Added
    if (cleanQuoteId) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const performedBy = user?.username || user?.email || 'System';
        
        await axios.post(
          `${API_URL}/api/quote-activity-logs`,
          {
            quote_id: cleanQuoteId,
            action_type: 'product_added',
            description: `Vehicle added: ${make || ''} ${model || ''}`.trim() || 'Vehicle',
            metadata: {
              productType: 'vehicle',
              performedBy: performedBy,
              make: make || '',
              model: model || '',
              registrationNumber: registrationNumber || '',
              year: year || '',
              insuredValue: parseFloat(insuredValue) || 0,
              addedAt: new Date().toISOString()
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Activity logged: vehicle_added');
      } catch (logError) {
        console.error('❌ Failed to log vehicle addition:', logError);
        // Don't fail the addition if logging fails
      }
    }
  }
  
  // ✅ FIX: Navigate based on mode
  setTimeout(() => {
    // ... navigation code (keep as is)
  }, 1500);
}

    } catch (error) {
      console.error('Error saving vehicle:', error);
      setLocalSaveMessage(`Failed to save vehicle: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Clear saved form data after successful save
      localStorage.removeItem(`vehicle_form_draft_${clientId}`);
      setIsSaving(false);
    }
  };
  
    const handleOpenPersonModal = () => {
  // Save current form data to localStorage before navigating
  const vehicleFormData = {
    vehicleType,
    registrationNumber,
    make,
    model,
    year,
    variant,
    color,
    usage,
    vinNumber,
    engineNumber,
    mmcode,
    insuredValue,
    selectedCoverType,
    useRetailValue,
    excessBuyBack,
    creditShortfall,
    carHire,
    selectedParking,
    selectedNightParking,
    selectedOwner,
    selectedDriver,
    selectedOtherDrivers,
    selectedSafetyFeatures,
    addressLine1,
    addressLine2,
    suburb,
    cityTown,
    postalCode,
    region,
    country,
    overnightParkingElsewhere,
    premium,
    excess,
    selectedNoClaimBonus,
    overnightAddress,
    currencyDisplay
  };
  
  localStorage.setItem(`vehicle_form_draft_${clientId}`, JSON.stringify(vehicleFormData));
  console.log('💾 Saved vehicle form data to localStorage');
  
  // Pass the current quoteId and returnTo parameter so we come back here
  const returnUrl = `/add-vehicle/${quoteId || ''}?clientId=${clientId}&returnTo=${encodeURIComponent(returnTo || '')}`;
  navigate(`/add-person/${clientId}?quoteId=${quoteId || ''}&returnTo=${encodeURIComponent(returnUrl)}`);
};
  
  if (!user) return <div>Loading...</div>;
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Vehicle Details' : 'Add Vehicle Details'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">

            <button
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="relative bg-white p-10 rounded-lg shadow-xl w-full max-w-4xl">
              <h2 className="text-2xl font-bold mb-6">
                {editingId ? 'Edit Vehicle Details' : 'Add Vehicle Details'}
              </h2>
              
              {localSaveMessage && (
                <div className={`mb-4 p-3 rounded ${localSaveMessage.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {localSaveMessage}
                </div>
              )}
              
              {/* Vehicle Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Vehicle Type</label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="car">Car</option>
                    <option value="motorcycle">Motorcycle</option>
                    <option value="LDV<1400">LDV greater than 1400</option>
                    <option value="LDV>1400">LDV less than 1400</option>
                    <option value="4X2 Double Cap">4X2 Double Cap</option>
                    <option value="4X2 Single Cap">4X2 Single Cap</option>
                    <option value="4X4 Double cab">4X4 Double cab</option>
                    <option value="4X4 Single Cap">4X4 Single Cap</option>
                    <option value="4X4 Luxuty">4X4 Luxuty</option>
                    <option value="Kombi">Kombi</option>
                    <option value="Venture">Venture</option>
                    <option value="SUV">SUV</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Reg. Number</label>
                  <input type="text" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., ABC 123 GP" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Make</label>
                  <input type="text" value={make} onChange={(e) => setMake(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Toyota" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Model</label>
                  <input type="text" value={model} onChange={(e) => setModel(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Corolla" />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Year</label>
                  <YearPicker
                    value={year ? parseInt(year) : undefined}
                    onChange={(selectedYear) => setYear(selectedYear.toString())}
                    disabled={false}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Variant</label>
                  <input type="text" value={variant} onChange={(e) => setVariant(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., 1.8L" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Color</label>
                  <input type="text" value={color} onChange={(e) => setColor(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Blue" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Vehicle Usage</label>
                  <select value={usage} onChange={(e) => setUsage(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="">Select Usage</option>
                    <option value="Business">Business</option>
                    <option value="Private">Private</option>
                    <option value="Combined">Combined</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">VIN Number</label>
                  <input type="text" value={vinNumber} onChange={(e) => setVinNumber(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., JT2FA22D1X06xxxxx" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Engine Number</label>
                  <input type="text" value={engineNumber} onChange={(e) => setEngineNumber(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., 1KRFE08xxxxx" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">MMCode</label>
                  <input type="text" value={mmcode} onChange={(e) => setMmcode(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., MM00123" />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Cover Type</label>
                  <select value={selectedCoverType} onChange={(e) => setSelectedCoverType(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="" disabled>Select</option>
                    {coverTypes.map(cover => (<option key={cover.id} value={cover.id}>{cover.name}</option>))}
                  </select>
                </div>
              </div>
              
              {/* Insured Value and No Claim Bonus */}
              <div className="flex space-x-6 mb-6">
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="text-gray-500">R</span></div>
                    <input type="text" value={currencyDisplay.insuredValue} onChange={(e) => { const val = e.target.value; setCurrencyDisplay(prev => ({ ...prev, insuredValue: formatCurrencyInput(val) })); setInsuredValue(parseCurrencyInput(val).toString()); }} className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="0.00" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">No Claim Bonus</label>
                  <select value={selectedNoClaimBonus} onChange={(e) => setSelectedNoClaimBonus(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="" disabled>Select bonus</option>
                    {noClaimBonusOptions.map(option => (<option key={option.id} value={option.id}>{option.name}</option>))}
                  </select>
                </div>
              </div>
              
              {/* Cover Type Checkboxes */}
              <div className="mt-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center"><input type="checkbox" checked={useRetailValue} onChange={(e) => setUseRetailValue(e.target.checked)} className="form-checkbox h-4 w-4 text-blue-600 rounded" /><label className="ml-2 text-gray-700 text-sm">Use retail value</label></div>
                  <div className="flex items-center"><input type="checkbox" checked={excessBuyBack} onChange={(e) => setExcessBuyBack(e.target.checked)} className="form-checkbox h-4 w-4 text-blue-600 rounded" /><label className="ml-2 text-gray-700 text-sm">Excess Buy Back</label></div>
                  <div className="flex items-center"><input type="checkbox" checked={creditShortfall} onChange={(e) => setCreditShortfall(e.target.checked)} className="form-checkbox h-4 w-4 text-blue-600 rounded" /><label className="ml-2 text-gray-700 text-sm">Credit Shortfall</label></div>
                  <div className="flex items-center"><input type="checkbox" checked={carHire} onChange={(e) => setCarHire(e.target.checked)} className="form-checkbox h-4 w-4 text-blue-600 rounded" /><label className="ml-2 text-gray-700 text-sm">Car Hire</label></div>
                </div>
              </div>
              
              {/* Parking Selection */}
              <div className="flex space-x-4 mt-6 mb-6">
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Select parking locations for during the day</label>
                  <select value={selectedParking} onChange={(e) => setSelectedParking(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="" disabled>Select a location</option>
                    {parkingLocations.map(location => (<option key={location.id} value={location.id}>{location.name}</option>))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Select parking locations for during the night</label>
                  <select value={selectedNightParking} onChange={(e) => setSelectedNightParking(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="" disabled>Select a location</option>
                    {parkingLocations.map(location => (<option key={location.id} value={location.id}>{location.name}</option>))}
                  </select>
                </div>
              </div>
              
              {/* Person of Interest Buttons */}
              <div className="mb-6 flex space-x-4">
                <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105" onClick={handleOpenPersonModal}>Create person of interest</button>
              </div>
              
              {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
              {isLoadingEffective && <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">Loading persons of interest...</div>}
              
              {/* Registered Owner and Regular Driver */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Registered Owner</label>
                  <select value={selectedOwner} onChange={(e) => setSelectedOwner(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" disabled={loading || !clientId}>
                    <option value="" disabled>Select a person</option>
                    {getAllOptions().map(option => (<option key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}>{option.displayName}{option.isClient ? ' (Client)' : ''}</option>))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Regular Driver</label>
                  <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" disabled={loading || !clientId}>
                    <option value="" disabled>Select a driver</option>
                    {getAllOptions().map(option => (<option key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}>{option.displayName}{option.isClient ? ' (Client)' : ''}</option>))}
                  </select>
                </div>
              </div>
              
              {/* Other Drivers and Safety Features */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Other Drivers</label>
                  <select value={selectedOtherDrivers} onChange={(e) => setSelectedOtherDrivers(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" disabled={loading || !clientId}>
                    <option value="" disabled>Select other drivers</option>
                    {getAllOptions().map(option => (<option key={option.isClient ? `client-${option.id}` : option.id?.toString() || ''} value={option.isClient ? `client-${option.id}` : option.id?.toString() || ''}>{option.displayName}{option.isClient ? ' (Client)' : ''}</option>))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Safety Features</label>
                  <select value={selectedSafetyFeatures} onChange={(e) => setSelectedSafetyFeatures(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm">
                    <option value="" disabled>Select safety features</option>
                    {safetyFeatures.map(feature => (<option key={feature.id} value={feature.id}>{feature.name}</option>))}
                  </select>
                </div>
              </div>
              
              {/* Fill from Client Button */}
              <div className="mb-6">
                <button className={`${addressLoading ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-700'} text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 w-full flex items-center justify-center`} onClick={handleFillFromClient} disabled={addressLoading}>
                  {addressLoading ? (<><span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>Fetching Address...</>) : ('Fill from Client')}
                </button>
                {clientError && !localSaveMessage && <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 text-sm rounded">{clientError}</div>}
                {!clientError && !clientAddress.address_line1 && !addressLoading && !localSaveMessage && <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 text-yellow-700 text-sm rounded">Note: No address data available for this client yet</div>}
              </div>
              
              {/* Address Fields */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Address Line 1</label><input type="text" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., 123 Main Street" /></div>
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Address Line 2</label><input type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Apt 4B, Suburb" /></div>
              </div>
              <div className="flex space-x-4 mb-6">
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Suburb</label><input type="text" value={suburb} onChange={(e) => setSuburb(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Sandton" /></div>
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">City/Town</label><input type="text" value={cityTown} onChange={(e) => setCityTown(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Johannesburg" /></div>
              </div>
              <div className="flex space-x-4 mb-6">
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Postal Code</label><input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., 2196" /></div>
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Region</label><input type="text" value={region} onChange={(e) => setRegion(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., Gauteng" /></div>
              </div>
              <div className="mb-6"><label className="block text-gray-700 font-bold mb-2 text-sm">Country</label><input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="e.g., South Africa" /></div>
              
              {/* Overnight Parking */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Are any of the vehicles parked anywhere else than at the risk address overnight?</label>
                <div className="flex gap-6">
                  <label className="flex items-center"><input type="radio" name="overnightElsewhere" checked={overnightParkingElsewhere === true} onChange={() => setOvernightParkingElsewhere(true)} className="h-4 w-4 text-blue-600 focus:ring-blue-500" /><span className="ml-2 text-sm text-gray-700">Yes</span></label>
                  <label className="flex items-center"><input type="radio" name="overnightElsewhere" checked={overnightParkingElsewhere === false} onChange={() => { setOvernightParkingElsewhere(false); setOvernightAddress({ address_line1: '', address_line2: '', suburb: '', city_town: '', postal_code: '', region: '', country: '' }); }} className="h-4 w-4 text-blue-600 focus:ring-blue-500" /><span className="ml-2 text-sm text-gray-700">No</span></label>
                </div>
              </div>
              
              {overnightParkingElsewhere && (
                <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-gray-50 space-y-4">
                  <h4 className="text-md font-semibold text-gray-800">Overnight Parking Address</h4>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search Address (autocomplete)</label>
                    <input ref={overnightInputRef} type="text" onChange={handleOvernightSearchChange} placeholder="Start typing street address..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    {showOvernightSuggestions && overnightSuggestions.length > 0 && (<ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-auto shadow-lg">{overnightSuggestions.map((feature, idx) => (<li key={feature.id || idx} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm" onClick={() => handleOvernightAddressSelect(feature)}>{feature.place_name}</li>))}</ul>)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label><input type="text" value={overnightAddress.address_line1} onChange={(e) => handleOvernightAddressChange('address_line1', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label><input type="text" value={overnightAddress.address_line2} onChange={(e) => handleOvernightAddressChange('address_line2', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label><input type="text" value={overnightAddress.suburb} onChange={(e) => handleOvernightAddressChange('suburb', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label><input type="text" value={overnightAddress.city_town} onChange={(e) => handleOvernightAddressChange('city_town', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label><input type="text" value={overnightAddress.postal_code} onChange={(e) => handleOvernightAddressChange('postal_code', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Region/Province</label><input type="text" value={overnightAddress.region} onChange={(e) => handleOvernightAddressChange('region', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Country</label><input type="text" value={overnightAddress.country} onChange={(e) => handleOvernightAddressChange('country', e.target.value)} className="w-full px-3 py-2 border rounded-md" /></div>
                  </div>
                </div>
              )}
              
              {/* Premium and Excess */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Premium</label><div className="relative"><div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="text-gray-500">R</span></div><input type="text" value={currencyDisplay.premium} onChange={(e) => { const val = e.target.value; setCurrencyDisplay(prev => ({ ...prev, premium: formatCurrencyInput(val) })); setPremium(parseCurrencyInput(val).toString()); }} className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="0.00" /></div></div>
                <div className="flex-1"><label className="block text-gray-700 font-bold mb-2 text-sm">Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span></label><div className="relative"><div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="text-gray-500">R</span></div><input type="text" value={currencyDisplay.excess} onChange={(e) => { const val = e.target.value; setCurrencyDisplay(prev => ({ ...prev, excess: formatCurrencyInput(val) })); setExcess(parseCurrencyInput(val).toString()); }} className="shadow-sm border rounded w-full py-2 px-3 pl-8 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm" placeholder="0.00" /></div></div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end mt-6 space-x-4">

                <button 
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=vehicle&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }} 
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
>
  Cancel
</button>
                
                <button onClick={handleSaveClick} disabled={isSaving} className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {isSaving ? (<><span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>Saving...</>) : (editingId ? 'Update Vehicle' : 'Add Vehicle')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVehiclePage;